import { AbstractUser } from "@kvix/shared";
import { Button, Text } from "@kvix/ui";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormHelperText,
  Input,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { KvixUserContext } from "../../../../../../contexts/user";
import { emailValidation } from "../../../../../../utils/RegexValidation/emailValidation";

const useStyles = makeStyles((theme) => ({
  dialogContainer: {
    textAlign: "center",
    padding: "38px",
    margin: "auto",
    width: "100%",
  },
  dialogTitle: {
    whiteSpace: "pre-line",
    letterSpacing: "0",
    lineHeight: "44px",
  },
  dialogText: {
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },
  dialogInput: {
    fontWeight: 500,
    letterSpacing: "0.5px",
    background: theme.palette.type === "dark" ? "#425784" : "#FEE9EE",
    height: "44px",
    width: "100%",
    lineHeight: "24px",
  },
  dialogActions: {
    justifyContent: "center",
    flexDirection: "column",
    padding: "0 10%",
  },
  dialogButton: {
    width: "100%",
    margin: "0 0 10px 0",
  },
  dialogButtonClose: {
    width: "100%",
  },
}));

export const ChangeEmailDialog: React.FC<{
  open: boolean;
  user: AbstractUser;
  fullScreen: boolean;
  handleOpen: (value: boolean) => void;
}> = (props) => {
  const classes = useStyles(props);
  const [newEmail, setNewEmail] = useState("");
  const validEmailAddress = emailValidation(newEmail);
  const formError = newEmail.length > 0 && !validEmailAddress;
  const [requestError, setRequestError] = useState(false);
  const [emailUpdateStatus, setEmailUpdateStatus] = useState("");
  const { actions } = useContext(KvixUserContext);
  const { t, ready } = useTranslation(["account", "general"]);

  const requestEmailChange = async (email: string) => {
    try {
      setEmailUpdateStatus("loading");
      await actions.requestEmailChange(email);
      setEmailUpdateStatus("finished");
    } catch (error) {
      setEmailUpdateStatus("");
      setRequestError(true);
    }
  };

  const closeDialog = () => {
    props.handleOpen(false);
    setEmailUpdateStatus("");
  };

  if (!ready) {
    return null;
  }

  return (
    <Dialog
      open={props.open}
      onClose={closeDialog}
      fullScreen={props.fullScreen}
    >
      <div className={classes.dialogContainer}>
        {emailUpdateStatus !== "finished" ? (
          <>
            <Text className={classes.dialogTitle} variant="h3">
              {t("account.changeEmailDialog.title")}
            </Text>
            <DialogContent style={{ margin: "12px 0" }}>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.subtitle")}
                <b>{`${props.user.email}`}</b>
              </DialogContentText>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.text")}
              </DialogContentText>
            </DialogContent>
          </>
        ) : (
          <>
            <Text className={classes.dialogTitle} variant="h3">
              {t("account.changeEmailDialog.sentTitle")}
            </Text>
            <DialogContent style={{ margin: "12px 0" }}>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.sentSubtitle")}
                <b>{`${newEmail}.`}</b>
              </DialogContentText>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.text1")}
              </DialogContentText>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.text2")}
              </DialogContentText>
              <DialogContentText className={classes.dialogText}>
                {t("account.changeEmailDialog.text3")}
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions className={classes.dialogActions}>
          <FormControl style={{ width: "100%" }} error={formError}>
            {emailUpdateStatus !== "finished" && (
              <>
                <Input
                  type="email"
                  color="primary"
                  disableUnderline
                  autoFocus
                  className={classes.dialogInput}
                  placeholder={t("account.changeEmailDialog.inputPlaceholder")}
                  onChange={(event) => setNewEmail(event.target.value)}
                />
                {validEmailAddress ? (
                  <FormHelperText
                    style={{
                      margin: "-2px 0px 18px 4px",
                      color: "green",
                    }}
                  >
                    {t("account.changeEmailDialog.inputValid")}
                  </FormHelperText>
                ) : (
                  <FormHelperText
                    style={{
                      margin: "-2px 0px 18px 4px",
                    }}
                  >
                    {t("account.changeEmailDialog.inputInvalid")}
                  </FormHelperText>
                )}

                <Button
                  onClick={() => requestEmailChange(newEmail)}
                  color="primary"
                  variant="contained"
                  disabled={!validEmailAddress}
                  className={classes.dialogButton}
                  loading={emailUpdateStatus === "loading"}
                >
                  {t("general:buttons.continue")}
                </Button>
              </>
            )}
            <Button
              onClick={closeDialog}
              color="primary"
              variant="text"
              className={classes.dialogButtonClose}
            >
              {emailUpdateStatus !== "finished"
                ? t("general:buttons.cancel")
                : t("general:buttons.close")}
            </Button>
          </FormControl>
        </DialogActions>
        {requestError && (
          <Alert severity="error">{t("general:errors.somethingWrong")}</Alert>
        )}
      </div>
    </Dialog>
  );
};
